import { Link } from "wouter";
import {
  CASE_NEXT_STEP,
  CASE_STATUS_MEANING,
  NO_ESTIMATE_NOTE,
  NO_NOTIFICATION_NOTE,
  NO_ONWARD_STEP,
  NO_ONWARD_STEP_NOTE,
  WAITING_ON_LABEL,
} from "@titleverify/shared";
import type { Case } from "../api.js";

/**
 * WHERE THIS REQUEST IS, AND WHOSE MOVE IT IS.
 * ===========================================================================
 *
 * The client's surfaces rendered `caseRow.status` as a bare token — `VALIDATING`,
 * `ROUTED` — and left the reader to work out what it meant and whether anyone
 * was doing anything. That gap gets filled by whatever the reader assumes, and
 * on a platform whose first non-negotiable is that it never states what it has
 * not established, leaving the most-read fact on the page to inference is the
 * same failure by omission.
 *
 * Every sentence comes from CASE_STATUS_MEANING / CASE_NEXT_STEP in
 * packages/shared. Nothing is derived, softened or re-worded here. The four
 * rules those maps are written under (no time, no fact about the land, no
 * invented automation, no promise for an unbuilt step) are documented on the
 * file itself, and this component's job is only to choose the panel.
 *
 * CLIENT ONLY, and that is about voice rather than permission. The strings are
 * addressed to the client in the second person — "Nothing is needed from you",
 * "the attorney on your case" — which is exactly wrong read by the attorney
 * doing the work or the operator routing it. Both of those roles get the raw
 * token in the case detail list, which is the right register for them.
 *
 * NO PROGRESS BAR, NO STEP COUNTER, NO PERCENTAGE, AND NO DATE. A bar implies
 * both a fixed path and a rate of travel, and the case machine has neither: it
 * is a graph, NEEDS_INFO is reachable from every active state, and UNDER_REVIEW
 * can legitimately fall back to SEARCH_IN_PROGRESS (packages/shared/workflow.ts).
 * Drawing four-of-nine against a case that may loop would be a moving picture of
 * a journey the platform cannot promise. NO_ESTIMATE_NOTE says so rather than
 * letting the absence read as an unfinished screen.
 */
export function CaseProgress({ caseRow }: { caseRow: Case }) {
  const meaning = CASE_STATUS_MEANING[caseRow.status];
  const step = CASE_NEXT_STEP[caseRow.status];

  /*
   * .notice when the client has something to do, .outcome otherwise.
   *
   * BOTH ARE NEUTRAL AND NEITHER IS [role="alert"], which is the decision that
   * matters. A request sitting at NEEDS_INFO has not gone wrong — it is a
   * first-class outcome (CLAUDE.md non-negotiable 1) and the case was created
   * and kept. Danger colouring would tell the client the opposite of what
   * happened. What the .notice buys is that the one state needing them is not
   * visually identical to the states that do not.
   *
   * The .status-* palette is not used anywhere here. It is a judgement about a
   * person's verification standing; borrowing it for a workflow state would tell
   * the reader the platform thinks their case being at this step is good or bad
   * news, and it holds no such view.
   */
  const panel = step.waitingOn === "client" ? "notice" : "outcome";

  return (
    <div className={panel}>
      {/*
        The heading is whose move it is, not the status token. It is the one
        thing a person with a request in flight actually wants, and putting it
        first means they get it without decoding a vocabulary.
      */}
      <h3>{WAITING_ON_LABEL[step.waitingOn]}</h3>

      <dl>
        <dt>Progress</dt>
        <dd>
          {/*
            The raw token is kept, not replaced. It is what ops and the attorney
            see, what the API returns, and what a client would have to quote to
            ask us about their request — so the plain sentence sits beside it
            rather than hiding it.
          */}
          <span className="state">{caseRow.status}</span>
        </dd>

        <dt>What that means</dt>
        <dd>{meaning}</dd>

        <dt>What happens next</dt>
        <dd>{step.next}</dd>
      </dl>

      {/*
        WHERE THE PLATFORM'S RECORD STOPS.
        Only rendered for a status nothing can move a case out of. The operator
        already gets this disclosure on the ops screen; the client, who has more
        at stake and no other way to find out, did not. `NO_ONWARD_STEP` is
        pinned to the API's mounted routes by a test, so this sentence cannot
        survive the feature that makes it false.
      */}
      {NO_ONWARD_STEP.includes(caseRow.status) ? (
        <p>
          <small>{NO_ONWARD_STEP_NOTE}</small>
        </p>
      ) : null}

      <p>
        <small>{NO_ESTIMATE_NOTE}</small>
      </p>

      {/*
        The counterpart to NO_ESTIMATE_NOTE, and the reason a waiting client
        opens this page at all. Without it, someone who ticked the email consent
        box on the intake form is waiting for a message that no part of this
        system sends.
      */}
      <p>
        <small>{NO_NOTIFICATION_NOTE}</small>
      </p>

      <p>
        <small>
          <Link href="/data">What TitleVerify holds about you, and for how long</Link>
        </small>
      </p>
    </div>
  );
}
